import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { formatPatientDob } from '@/lib/dateUtils';
import { buildPreAuthAuthoringAudit } from '@/lib/surgeon-console/preAuthAuthoringBrain';
import { clinicalSeverityEvidenceText } from '@/lib/surgeon-console/evidenceValidation';
import type { ClinicalSeverityData } from '@/lib/clinicalSeverityTypes';
import type { CaseCptLine, ScheduledCase } from '@/lib/templates/types';

export type IssueCategory = 'Coding' | 'Documentation' | 'Clinical_Threshold';
export type ResolutionType = 'auto_fix_code' | 'require_upload' | 'propose_addendum' | 'suggest_fix' | 'manual_dropdown';

export interface ActionableIssue {
  category: IssueCategory;
  severity: 'critical' | 'warning';
  code: string | null;
  description: string;
  resolution_type: ResolutionType;
  proposed_fix_data: string | null;
  /** 2-3 sentence explanation of HOW to satisfy this issue (what to do, what evidence supports it, why it resolves the payer concern). */
  detailed_fix_explanation?: string;
  /** When deletion of the offending code is also a valid resolution, the deletion option as a short imperative. */
  deletion_alternative?: string | null;
  /** True if office manager / RCM team can close this gap post-submission without surgeon involvement. */
  is_surgeon_bypassable?: boolean;
  /** One sentence: what RCM does post-submission (when bypassable) or why surgeon must decide first (when not). */
  bypass_reasoning?: string;
}

export interface DenialRiskResult {
  verdict: 'APPROVED' | 'BLOCKED';
  actionable_issues: ActionableIssue[];
  summary: string;
}

interface AnalysisPayload {
  selectedCase: ScheduledCase;
  cptLines: CaseCptLine[];
  defenderAnswers: Record<string, string>;
  clinicalNotes: string;
  procedureTarget?: string;
  diagnosisCodes?: Array<{ code: string; description: string }>;
  clinicalSeverityData?: ClinicalSeverityData | null;
  /** Denial Defender evaluation — exemption flags and gate results */
  denialDefenderResults?: {
    payerFlags?: {
      bone_on_bone_exemption?: boolean;
      acute_fracture_avulsion?: boolean;
      neurologic_deficit?: boolean;
      extended_therapy_met?: boolean;
    };
    gateResults?: Array<{ gate: string; passed: boolean; reason: string }>;
    requiredPTWeeks?: number;
    exemptionActive?: boolean;
  } | null;
}

function cptCode(line: Pick<CaseCptLine, 'code'>): string {
  return String(line.code);
}

function normalizeText(value: string | null | undefined): string {
  return (value || '').toLowerCase();
}

function hasModifier(line: CaseCptLine, modifier: string): boolean {
  return line.modifiers.some(item => item.trim().replace(/^-/, '').toUpperCase() === modifier);
}

export function buildDenialRiskClinicalNotesPayload(payload: AnalysisPayload): string {
  const docText = (payload.selectedCase.pre_loaded_docs || [])
    .map(doc => [doc.type, doc.extracted_text].filter(Boolean).join(': '))
    .filter(Boolean)
    .join('\n\n');

  return [
    payload.clinicalNotes,
    clinicalSeverityEvidenceText(payload.clinicalSeverityData),
    docText,
  ]
    .filter(Boolean)
    .join('\n\n');
}

function payloadHasShoulderMriEvidence(payload: AnalysisPayload): boolean {
  const text = normalizeText(buildDenialRiskClinicalNotesPayload(payload));
  return /\bmri\b/i.test(text) &&
    /(full[- ]thickness|supraspinatus|rotator cuff|infraspinatus|biceps)/i.test(text);
}

function payloadHasDistinctShoulderXsEvidence(payload: AnalysisPayload): boolean {
  const codes = payload.cptLines.map(cptCode);
  const hasRcr = codes.includes('29827');
  const hasSubacromialXs = payload.cptLines.some(line => cptCode(line) === '29826' && hasModifier(line, 'XS'));
  const hasBicepsXs = payload.cptLines.some(line => cptCode(line) === '29828' && hasModifier(line, 'XS'));
  const text = normalizeText(buildDenialRiskClinicalNotesPayload(payload));
  const subacromialEvidence = /(subacromial|acromion|acromioplasty|type iii|hooked|impingement|outlet)/i.test(text);
  const bicepsEvidence = /(biceps|long head|tenodesis|tendinosis|subluxation)/i.test(text);

  return hasRcr && subacromialEvidence && bicepsEvidence && (hasSubacromialXs || hasBicepsXs);
}

function isFalseMissingMriIssue(issue: ActionableIssue, payload: AnalysisPayload): boolean {
  if (!payloadHasShoulderMriEvidence(payload)) return false;
  const text = normalizeText(`${issue.description} ${issue.detailed_fix_explanation || ''} ${issue.proposed_fix_data || ''}`);
  return /\bmri\b/.test(text) && /(no|missing|attach|upload|required|documentation gap)/i.test(text);
}

function isFalseShoulderXsIssue(issue: ActionableIssue, payload: AnalysisPayload): boolean {
  if (!payloadHasDistinctShoulderXsEvidence(payload)) return false;
  const text = normalizeText(`${issue.code || ''} ${issue.description} ${issue.detailed_fix_explanation || ''} ${issue.proposed_fix_data || ''}`);
  return /(29826|29828)/.test(text) &&
    /\bxs\b/.test(text) &&
    /(remove|strip|should not|not carry|inclusive|bundled|bundling|ncci)/i.test(text);
}

export function normalizeDenialRiskResultForDeterministicEvidence(
  result: DenialRiskResult,
  payload: AnalysisPayload,
): DenialRiskResult {
  const filteredIssues = result.actionable_issues.filter(issue =>
    !isFalseMissingMriIssue(issue, payload) &&
    !isFalseShoulderXsIssue(issue, payload),
  );

  if (filteredIssues.length === result.actionable_issues.length) {
    return result;
  }

  const hasCritical = filteredIssues.some(issue => issue.severity === 'critical');
  return {
    ...result,
    verdict: hasCritical ? 'BLOCKED' : 'APPROVED',
    actionable_issues: filteredIssues,
    summary: hasCritical
      ? result.summary
      : 'RCM audit approved after deterministic packet evidence cleared false MRI and shoulder XS modifier findings.',
  };
}

function buildDemoDenialRiskResult(payload: AnalysisPayload): DenialRiskResult {
  const codes = payload.cptLines.map(line => {
    const modifiers = line.modifiers.length ? `-${line.modifiers.join(',')}` : '';
    return `${cptCode(line)}${modifiers}`;
  });
  const clinicalNotes = buildDenialRiskClinicalNotesPayload(payload);

  const audit = buildPreAuthAuthoringAudit({
    selectedCase: payload.selectedCase,
    template: {
      id: 'demo-risk-template',
      surgeon_id: payload.selectedCase.surgeon_id || 'demo-surgeon',
      template_name: payload.procedureTarget || payload.selectedCase.scheduled_procedure || 'Scheduled procedure',
      specialty: 'orthopedics',
      body_region: null,
      laterality_required: true,
      primary_cpts: [],
      secondary_cpts: [],
      narrative_template: null,
      common_addons: [],
      diagnosis_codes: payload.diagnosisCodes || [],
      metadata: {},
      version: 1,
      is_active: true,
      is_demo: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    },
    cptLines: payload.cptLines,
    laterality: null,
    clinicalText: clinicalNotes,
    clinicalSeverityData: payload.clinicalSeverityData,
  });

  const issues: ActionableIssue[] = [...audit.blockers, ...audit.surgeonQuestions].map(item => ({
    category: item.id.includes('cpt') || item.id.includes('modifier') || item.id.includes('code') || item.affectedCodes?.length
      ? 'Coding'
      : item.id.includes('therapy') || item.id.includes('mri') || item.id.includes('site') || item.id.includes('diagnosis')
        ? 'Documentation'
        : 'Clinical_Threshold',
    severity: item.severity === 'blocker' ? 'critical' : 'warning',
    code: item.affectedCodes?.[0] || null,
    description: `${item.title}: ${item.finding}`,
    resolution_type: item.recommendedCpt ? 'auto_fix_code' : item.suggestedLanguage ? 'propose_addendum' : 'suggest_fix',
    proposed_fix_data: item.recommendedCpt?.code || item.suggestedLanguage || item.prevention,
    detailed_fix_explanation: `${item.prevention} ${item.payerLogic}`,
    deletion_alternative: item.id === 'shoulder-slap-biceps-overlap'
      ? 'Remove either CPT 29807 or CPT 23430 after surgeon selects the correct biceps-labral strategy'
      : null,
    is_surgeon_bypassable: false,
    bypass_reasoning: 'This must be corrected before the payer-facing pre-authorization packet is generated.',
  }));

  if (issues.length > 0) {
    return {
      verdict: 'BLOCKED',
      actionable_issues: issues,
      summary: audit.summary,
    };
  }

  return {
    verdict: 'APPROVED',
    actionable_issues: [],
    summary: [
      'Demo RCM audit completed locally because the live analyzer was unavailable.',
      `Reviewed ${codes.join(', ') || 'selected CPT lines'} against the current packet narrative.`,
      payload.denialDefenderResults?.exemptionActive
        ? 'Urgent or exemption pathway is active from Denial Defender inputs.'
        : 'No blocking coding or documentation issue was produced in demo fallback.',
    ].join(' '),
  };
}

export function useDenialRiskAnalysis() {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [result, setResult] = useState<DenialRiskResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const analyze = useCallback(async (payload: AnalysisPayload): Promise<DenialRiskResult | null> => {
    setIsAnalyzing(true);
    setError(null);
    setResult(null);

    try {
      const cpt_codes = payload.cptLines.map(line => ({
        code: cptCode(line),
        description: line.description,
        modifiers: line.modifiers,
        section: line.section,
      }));

      const icd_10_codes: string[] = (payload.diagnosisCodes || []).map(d => d.code).filter(Boolean);
      const clinicalNotesPayload = buildDenialRiskClinicalNotesPayload(payload);

      const conservative_therapy: Record<string, string> = {};
      Object.entries(payload.defenderAnswers).forEach(([key, val]) => {
        if (key.includes('pt-duration')) conservative_therapy.pt_duration = val;
        if (key.includes('injection')) conservative_therapy.injection_type = val;
        if (key.includes('xray')) conservative_therapy.imaging_confirmed = val;
      });

      const body = {
        procedure_target: payload.procedureTarget ?? payload.selectedCase.scheduled_procedure ?? null,
        cpt_codes,
        icd_10_codes,
        patient_biomarkers: {},
        conservative_therapy,
        clinical_notes: clinicalNotesPayload,
        defender_answers: payload.defenderAnswers,
        patient_demographics: {
          name: payload.selectedCase.patient_name,
          dob: formatPatientDob(payload.selectedCase.patient_dob),
          mrn: payload.selectedCase.patient_mrn,
          payer: payload.selectedCase.payer_name,
          plan: payload.selectedCase.plan_name,
        },
        denial_defender_results: payload.denialDefenderResults ?? null,
      };

      const { data, error: fnError } = await supabase.functions.invoke('analyze-pre-auth-risk', {
        body,
      });

      if (fnError) {
        throw new Error(fnError.message || 'Edge function call failed');
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      const analysisResult = normalizeDenialRiskResultForDeterministicEvidence(data as DenialRiskResult, payload);
      setResult(analysisResult);
      return analysisResult;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error during denial risk analysis';
      if (payload.selectedCase.is_demo) {
        const demoResult = buildDemoDenialRiskResult(payload);
        setResult(demoResult);
        console.info('Denial risk analysis used deterministic demo fallback:', msg);
        return demoResult;
      }
      setError(msg);
      console.error('Denial risk analysis failed:', err);
      return null;
    } finally {
      setIsAnalyzing(false);
    }
  }, []);

  const clearResult = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return { isAnalyzing, result, error, analyze, clearResult };
}
